import apiClient from '../../shared/services/apiClient';

/**
 * CDN Service (User Version)
 *
 * Handles user-level read-only CDN operations.
 * Users can view CDN resources but cannot modify them.
 *
 * All routes use /api/user/cdn/* prefix
 */
class CDNService {
  // ==========================================
  // Domain Management (Full CRUD)
  // ==========================================

  /**
   * Get all domains
   */
  async getDomains() {
    try {
      const response = await apiClient.get('/api/user/cdn/domains');
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Get domain details with hostnames and rules
   * @param {string|number} domainOrId - Domain name (string) or domain ID (number)
   */
  async getDomain(domainOrId) {
    try {
      const params = typeof domainOrId === 'number' || /^\d+$/.test(domainOrId)
        ? { id: domainOrId }
        : { domain: domainOrId };
      const response = await apiClient.get('/api/user/cdn/domains/get', { params });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Create a new domain
   */
  async createDomain(data) {
    try {
      const response = await apiClient.post('/api/user/cdn/domains/create', data);
      return response.data;
    } catch (error) {
      return { ...(error.response?.data || {}), success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * 立即检查一次域名归属 → {verified, txt_name, txt_value, deadline}
   */
  async verifyDomain(id) {
    try {
      const response = await apiClient.post('/api/user/cdn/domains/verify', { id }, { skipGlobalErrorToast: true });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Update domain
   */
  async updateDomain(data) {
    try {
      const response = await apiClient.post('/api/user/cdn/domains/update', data);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  // 2026-10-07 套餐绑定：我的 CDN 套餐（容量/已绑域名）、换绑、确认 CNAME 已切换
  async getMySubscriptions() {
    try {
      const response = await apiClient.get('/api/user/cdn/subscriptions');
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message, data: [] };
    }
  }

  async confirmCnameSwitch(domainId) {
    try {
      const response = await apiClient.post('/api/user/cdn/domains/confirm-cname-switch', { domain_id: domainId }, { skipGlobalErrorToast: true });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.error || error.message };
    }
  }

  /**
   * Delete domain
   */
  async deleteDomain(id) {
    try {
      const response = await apiClient.delete('/api/user/cdn/domains/delete', { data: { id } });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Get Cloudflare accounts
   */
  async getCloudflareAccounts() {
    try {
      const response = await apiClient.get('/api/user/cdn/domains/cloudflare_accounts');
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Get Cloudflare zones for an account
   */
  async getCloudflareZones(accountId) {
    try {
      const response = await apiClient.get('/api/user/cdn/domains/cloudflare_zones', {
        params: { account_id: accountId }
      });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  // ==========================================
  // Hostname Management (Full CRUD)
  // ==========================================

  /**
   * Get all hostnames for a domain
   */
  async getHostnameQuota(params) {
    try {
      const response = await apiClient.get('/api/user/cdn/hostnames/quota', { params });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  async getHostnames(domainId) {
    try {
      const response = await apiClient.get('/api/user/cdn/hostnames', {
        params: { domain_id: domainId }
      });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Get hostname details
   */
  async getHostname(id) {
    try {
      const response = await apiClient.get('/api/user/cdn/hostnames/get', { params: { id } });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Create a new hostname
   */
  async createHostname(data) {
    try {
      const response = await apiClient.post('/api/user/cdn/hostnames/create', data);
      return response.data;
    } catch (error) {
      return { ...(error.response?.data || {}), success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * 托管 DNS 里已有解析的名字(加入 CDN 后会改成走 CDN)
   */
  async getDnsConflicts(hostnames) {
    try {
      const response = await apiClient.post('/api/user/cdn/hostnames/dns-conflicts', { hostnames });
      return response.data?.data || [];
    } catch {
      return [];
    }
  }

  /**
   * Update hostname
   */
  async updateHostname(data) {
    try {
      const response = await apiClient.post('/api/user/cdn/hostnames/update', data);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Delete hostname
   */
  async deleteHostname(id) {
    try {
      const response = await apiClient.delete('/api/user/cdn/hostnames/delete', { data: { id } });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  // ==========================================
  // Cache Purge
  // ==========================================

  /**
   * Purge CDN cache for a domain
   * @param {number} domainId - Domain ID
   * @param {string[]|null} paths - Optional array of URL paths to purge; null means purge all
   */
  async purgeCache(domainId, paths = null) {
    try {
      const payload = { id: domainId };
      if (paths && paths.length > 0) {
        payload.paths = paths;
      }
      const response = await apiClient.post('/api/user/cdn/domains/purge-cache', payload);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  // ==========================================
  // Rules Management (Full CRUD)
  // ==========================================

  /**
   * Get rules for a domain
   * @param {string|number} domainOrId - Domain name (string) or domain ID (number)
   */
  async getRules(domainOrId) {
    try {
      const params = typeof domainOrId === 'number'
        ? { domain_id: domainOrId }
        : { domain: domainOrId };
      const response = await apiClient.get('/api/user/cdn/rules', { params });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Get rule details
   */
  async getRule(id) {
    try {
      const response = await apiClient.get('/api/user/cdn/rules/get', { params: { id } });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Create a new rule
   */
  async createRule(data) {
    try {
      const response = await apiClient.post('/api/user/cdn/rules/create', data);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Update rule
   */
  async updateRule(data) {
    try {
      const response = await apiClient.post('/api/user/cdn/rules/update', data);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Delete rule
   */
  async deleteRule(id) {
    try {
      const response = await apiClient.delete('/api/user/cdn/rules/delete', { data: { id } });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  // ==========================================
  // WAF Management (Read-Only)
  // ==========================================

  /**
   * Get all WAF rules
   */
  async getWafRules() {
    try {
      const response = await apiClient.get('/api/user/cdn/waf');
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Get WAF rule details
   */
  async getWafRule(id) {
    try {
      const response = await apiClient.get('/api/user/cdn/waf/get', { params: { id } });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  // ==========================================
  // SSL Certificate Management (Full CRUD)
  // ==========================================

  /**
   * Get all SSL certificates
   */
  async getSSLCertificates() {
    try {
      const response = await apiClient.get('/api/user/cdn/ssl');
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Get SSL certificate for domain
   */
  async getSSLCertificate(domain) {
    try {
      const response = await apiClient.get('/api/user/cdn/ssl/get', { params: { domain } });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Get ACME queue status
   */
  async getSSLQueue() {
    try {
      const response = await apiClient.get('/api/user/cdn/ssl/queue');
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Request SSL certificate (ACME)
   */
  async requestSSLCertificate(data) {
    try {
      const response = await apiClient.post('/api/user/cdn/ssl/request', data);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Renew SSL certificate
   */
  async renewSSLCertificate(data) {
    try {
      const response = await apiClient.post('/api/user/cdn/ssl/renew', data);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Revoke SSL certificate
   */
  async revokeSSLCertificate(data) {
    try {
      const response = await apiClient.post('/api/user/cdn/ssl/revoke', data);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Sync certificate from provider
   */
  async syncSSLCertificate(data) {
    try {
      const response = await apiClient.post('/api/user/cdn/ssl/sync_cert', data);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Sync ACME challenge
   */
  async syncACMEChallenge(data) {
    try {
      const response = await apiClient.post('/api/user/cdn/ssl/sync-challenge', data);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Get certificate configuration
   */
  async getCertConfig(domain) {
    try {
      const response = await apiClient.get('/api/user/cdn/ssl/get-cert-config', { params: { domain } });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Toggle SSL on/off for domain
   */
  async toggleSSL(data) {
    try {
      const response = await apiClient.post('/api/user/cdn/ssl/toggle', data);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Upload custom SSL certificate
   */
  async uploadSSLCertificate(data) {
    try {
      const response = await apiClient.post('/api/user/cdn/ssl/upload', data);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Get SSL providers (for shop)
   */
  async getSSLProviders() {
    try {
      const response = await apiClient.get('/api/user/ssl/providers');
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Get SSL products (for shop)
   */
  async getSSLProducts(providerId = null) {
    try {
      const params = providerId ? { provider_id: providerId } : {};
      const response = await apiClient.get('/api/user/ssl/products', { params });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  // ==========================================
  // Logs & Analytics (Read-Only)
  // ==========================================

  /**
   * Get traffic statistics by country
   */
  async getTrafficStats(startTime, endTime, domainId = null) {
    try {
      const response = await apiClient.get('/api/user/cdn/logs/traffic', {
        params: {
          startTime,
          endTime,
          domain_id: domainId
        }
      });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Get HTTP status code statistics
   */
  async getStatusStats(startTime, endTime, domainId = null) {
    try {
      const response = await apiClient.get('/api/user/cdn/logs/status', {
        params: {
          startTime,
          endTime,
          domain_id: domainId
        }
      });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Get cache performance statistics
   */
  async getCacheStats(startTime, endTime, domainId = null) {
    try {
      const response = await apiClient.get('/api/user/cdn/logs/cache', {
        params: {
          startTime,
          endTime,
          domain_id: domainId
        }
      });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Get domain statistics
   */
  async getDomainStats(startTime, endTime) {
    try {
      const response = await apiClient.get('/api/user/cdn/logs/domains', {
        params: {
          startTime,
          endTime
        }
      });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Get top URLs
   */
  async getTopUrls(startTime, endTime, domainId = null, limit = 10) {
    try {
      const response = await apiClient.get('/api/user/cdn/logs/top_urls', {
        params: {
          startTime,
          endTime,
          domain_id: domainId,
          limit
        }
      });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Get browser statistics
   */
  async getBrowserStats(startTime, endTime, domainId = null) {
    try {
      const response = await apiClient.get('/api/user/cdn/logs/browsers', {
        params: {
          startTime,
          endTime,
          domain_id: domainId
        }
      });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  // ==========================================
  // Account Settings (Read-Only)
  // ==========================================

  /**
   * Get account CDN settings
   */
  async getAccountSettings() {
    try {
      const response = await apiClient.get('/api/user/cdn/account-settings');
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  // ==========================================
  // Error Pages (Full CRUD)
  // ==========================================

  /**
   * Get error pages
   */
  async getErrorPages() {
    try {
      const response = await apiClient.get('/api/user/cdn/error-pages');
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Get error page templates
   */
  async getErrorPageTemplates() {
    try {
      const response = await apiClient.get('/api/user/cdn/error-pages/templates');
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Get specific error page by type
   * @param {string} pageType - Error page type (e.g., '404', '500', etc.)
   */
  async getErrorPage(pageType) {
    try {
      const response = await apiClient.get(`/api/user/cdn/error-pages/${pageType}`);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Save (create or update) error page
   * @param {object} errorPageData - Error page data (page_type, content, etc.)
   */
  async saveErrorPage(errorPageData) {
    try {
      const response = await apiClient.post('/api/user/cdn/error-pages/save', errorPageData);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Delete error page by ID
   * @param {number} id - Error page ID
   */
  async deleteErrorPage(id) {
    try {
      const response = await apiClient.delete('/api/user/cdn/error-pages/delete', { data: { id } });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  // ==========================================
  // WAF Configuration (Read-Only)
  // ==========================================

  /**
   * Get WAF configuration
   * @param {number} domainId - Optional domain ID for domain-level config
   */
  async getWafConfig(domainId = null) {
    try {
      const params = domainId ? { domain_id: domainId } : {};
      const response = await apiClient.get('/api/user/cdn/waf-config', { params });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Save WAF configuration (enabled_rulesets / mode / score_threshold / paranoia_level)
   * 之前组件调用 cdnService.saveWafConfig 但服务层未定义 → 保存恒失败；此处补齐。
   */
  async saveWafConfig(payload) {
    try {
      const response = await apiClient.post('/api/user/cdn/waf-config/save', payload);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  // WAF 分类开关(14 类;后端 WafCategoryCatalog)
  async getWafCategories(domainId) {
    try {
      const response = await apiClient.get('/api/user/cdn/waf-categories', { params: { domain_id: domainId } });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  async saveWafCategories(domainId, categories) {
    try {
      const response = await apiClient.post('/api/user/cdn/waf-categories/save', { domain_id: domainId, categories });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Get available WAF rulesets
   */
  async getWafRulesets() {
    try {
      const response = await apiClient.get('/api/user/cdn/waf-rulesets');
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Get WAF rulesets grouped by category
   */
  async getWafRulesetsGrouped(domainId) {
    try {
      const response = await apiClient.get('/api/user/cdn/waf-rulesets/grouped', { params: domainId ? { domain_id: domainId } : {} });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  // ==========================================
  // Cache Presets (Read-Only)
  // ==========================================

  /**
   * Get cache presets
   */
  async getCachePresets() {
    try {
      const response = await apiClient.get('/api/user/cdn/cache-presets');
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  // ==========================================
  // CDN Groups Management (Read-Only)
  // ==========================================

  /**
   * Get authorized CDN groups for the current user.
   * Backend filters by the caller's token identity (authorizedGroupIds);
   * no user_id is sent — it is ignored server-side.
   * @param {string} status - Optional status filter (active/inactive)
   */
  async getCdnGroups(status = null) {
    try {
      const params = {};
      if (status) params.status = status;
      const response = await apiClient.get('/api/user/cdn/groups', { params });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Get single CDN group
   * @param {number} id - Group ID
   */
  async getCdnGroup(id) {
    try {
      const response = await apiClient.get('/api/user/cdn/groups/get', { params: { id } });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Get available DNS zones for CDN groups (uid=1 only)
   */
  async getAvailableCdnZones() {
    try {
      const response = await apiClient.get('/api/user/cdn/groups/available-zones');
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  // ==========================================
  // CDN Lines Management (Read-Only)
  // ==========================================

  /**
   * Get all CDN lines
   * @param {number} groupId - Optional group ID filter
   * @param {string} status - Optional status filter
   */
  async getCdnLines(groupId = null, status = null) {
    try {
      const params = {};
      if (groupId) params.group_id = groupId;
      if (status) params.status = status;
      const response = await apiClient.get('/api/user/cdn/lines', { params });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Get single CDN line
   * @param {number} id - Line ID
   */
  async getCdnLine(id) {
    try {
      const response = await apiClient.get('/api/user/cdn/lines/get', { params: { id } });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  // ==========================================
  // CDN Workers Management (Full CRUD)
  // ==========================================

  /**
   * Get all workers
   * @param {number} domainId - Optional domain ID filter
   * @param {number} page - Page number (default: 1)
   * @param {number} pageSize - Page size (default: 20)
   */
  async getWorkers(domainId = null, page = 1, pageSize = 20) {
    try {
      const params = { page, page_size: pageSize };
      if (domainId) params.domain_id = domainId;
      const response = await apiClient.get('/api/user/cdn/workers', { params });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Get worker details
   * @param {number} id - Worker ID
   */
  async getWorker(id) {
    try {
      const response = await apiClient.get('/api/user/cdn/workers/detail', { params: { id } });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Save (create or update) worker
   * @param {object} workerData - Worker data (id for update, code, name, etc.)
   */
  async saveWorker(workerData) {
    try {
      const response = await apiClient.post('/api/user/cdn/workers/save', workerData);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Delete worker
   * @param {number} id - Worker ID
   */
  async deleteWorker(id) {
    try {
      const response = await apiClient.delete('/api/user/cdn/workers/delete', { data: { id } });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Toggle worker on/off
   * @param {number} id - Worker ID
   * @param {boolean} enabled - Enable/disable status
   */
  // ---- Cloudflare-style worker lifecycle ----
  async _wget(path, params) {
    try {
      const response = await apiClient.get(path, { params });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  async _wpost(path, data) {
    try {
      const response = await apiClient.post(path, data);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  getWorkerVersions(id) { return this._wget('/api/user/cdn/workers/versions', { id }); }
  getWorkerVersion(id, version) { return this._wget('/api/user/cdn/workers/version', { id, version }); }
  rollbackWorker(id, version) { return this._wpost('/api/user/cdn/workers/rollback', { id, version }); }
  saveWorkerVars(id, vars) { return this._wpost('/api/user/cdn/workers/vars', { id, vars }); }
  getWorkerRoutes(id) { return this._wget('/api/user/cdn/workers/routes', { id }); }
  previewWorker(payload) { return this._wpost('/api/user/cdn/workers/preview', payload); }

  async toggleWorker(id, enabled) {
    try {
      const response = await apiClient.post('/api/user/cdn/workers/toggle', { id, enabled });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Get workers for a specific domain
   * @param {string} domain - Domain name
   */
  async getDomainWorkers(domain) {
    try {
      const response = await apiClient.get(`/api/user/cdn/domains/${domain}/workers`);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Get available workers for a domain
   * @param {string} domain - Domain name
   */
  async getAvailableWorkers(domain) {
    try {
      const response = await apiClient.get(`/api/user/cdn/domains/${domain}/workers/available`);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Save (assign) worker to domain
   * @param {string} domain - Domain name
   * @param {object} workerData - Worker assignment data
   */
  async saveDomainWorker(domain, workerData) {
    try {
      const response = await apiClient.post(`/api/user/cdn/domains/${domain}/workers/save`, workerData);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Delete (unassign) worker from domain
   * @param {string} domain - Domain name
   * @param {number} id - Worker assignment ID
   */
  async deleteDomainWorker(domain, id) {
    try {
      const response = await apiClient.delete(`/api/user/cdn/domains/${domain}/workers/delete`, { data: { id } });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Toggle worker binding enabled status
   * @param {string} domain - Domain name
   * @param {number} id - Worker assignment ID
   * @param {boolean} enabled - New enabled status
   */
  async toggleDomainWorker(domain, id, enabled) {
    try {
      const response = await apiClient.post(`/api/user/cdn/domains/${domain}/workers/toggle`, { id, enabled });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  // ==========================================
  // Traffic Analytics (Read-Only)
  // ==========================================

  /**
   * Get traffic statistics overview
   */
  async getTrafficOverview(startTime, endTime, domainId = null) {
    try {
      const params = { startTime, endTime };
      if (domainId) params.domain_id = domainId;
      const response = await apiClient.get('/api/user/traffic/stats', { params });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Get traffic map data (by country/region)
   */
  async getTrafficMap(startTime, endTime, domainId = null) {
    try {
      const params = { startTime, endTime };
      if (domainId) params.domain_id = domainId;
      const response = await apiClient.get('/api/user/traffic/map', { params });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Get traffic cache performance  (time series)
   */
  async getTrafficCachePerformance(startTime, endTime, domainId = null) {
    try {
      const params = { startTime, endTime };
      if (domainId) params.domain_id = domainId;
      const response = await apiClient.get('/api/user/traffic/cache', { params });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Get top URLs
   */
  async getTrafficTopUrls(startTime, endTime, domainId = null, limit = 10) {
    try {
      const params = { startTime, endTime, limit };
      if (domainId) params.domain_id = domainId;
      const response = await apiClient.get('/api/user/traffic/top-urls', { params });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Get HTTP status codes statistics
   */
  async getTrafficStatusCodes(startTime, endTime, domainId = null) {
    try {
      const params = { startTime, endTime };
      if (domainId) params.domain_id = domainId;
      const response = await apiClient.get('/api/user/traffic/status-codes', { params });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Get browser statistics
   */
  async getTrafficBrowsers(startTime, endTime, domainId = null) {
    try {
      const params = { startTime, endTime };
      if (domainId) params.domain_id = domainId;
      const response = await apiClient.get('/api/user/traffic/browsers', { params });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  // ==========================================
  // Domain Worker Bindings (Read-Only)
  // ==========================================

  /**
   * Get domain worker bindings
   * @param {string} domain - Domain name
   */
  async getDomainWorkers(domain) {
    try {
      const response = await apiClient.get(`/api/user/cdn/domains/${domain}/workers`);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Get available workers for domain
   * @param {string} domain - Domain name
   */
  async getAvailableWorkers(domain) {
    try {
      const response = await apiClient.get(`/api/user/cdn/domains/${domain}/workers/available`);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * DNS check (NS/CNAME lookup)
   */
  async getDnsHosting(domain) {
    try {
      const response = await apiClient.get('/api/user/cdn/domains/dns-hosting', { params: { domain } });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  async getDnsCheck(params) {
    try {
      const response = await apiClient.get('/api/user/cdn/domains/dns-check', { params });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  // ==========================================
  // Hostname SSL Management
  // ==========================================

  /**
   * Enable or disable SSL for a hostname
   * @param {number} id - Hostname ID
   * @param {boolean} enabled - Enable/disable SSL
   * @param {string} sslType - SSL type: 'none', 'auto', 'custom'
   */
  async enableHostnameSsl(id, enabled, sslType = 'auto') {
    try {
      const response = await apiClient.post('/api/user/cdn/hostnames/ssl/enable', {
        id,
        enabled,
        ssl_type: sslType
      });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Request ACME certificate for a hostname
   * @param {number} id - Hostname ID
   * @param {string} validationType - 'http-01' or 'dns-01'
   */
  async requestHostnameAcme(id, validationType) {
    try {
      const response = await apiClient.post('/api/user/cdn/hostnames/ssl/request-acme', {
        id,
        validation_type: validationType
      });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Get ACME status for a hostname
   * @param {number} id - Hostname ID
   */
  async getHostnameAcmeStatus(id) {
    try {
      const response = await apiClient.get('/api/user/cdn/hostnames/ssl/acme-status', {
        params: { id }
      });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Upload custom SSL certificate for a hostname
   * @param {number} id - Hostname ID
   * @param {string} certificate - PEM certificate
   * @param {string} privateKey - PEM private key
   */
  async uploadHostnameSsl(id, certificate, privateKey) {
    try {
      const response = await apiClient.post('/api/user/cdn/hostnames/ssl/upload', {
        id,
        certificate,
        private_key: privateKey
      });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  // Certificate Library
  async getCertLibrary() {
    try {
      const response = await apiClient.get('/api/user/cdn/cert-library');
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  async uploadCertToLibrary(data) {
    try {
      const response = await apiClient.post('/api/user/cdn/cert-library/upload', data);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  async deleteCertFromLibrary(id) {
    try {
      const response = await apiClient.delete('/api/user/cdn/cert-library/delete', { data: { id } });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  async applyCertToHostname(certId, hostnameId) {
    try {
      const response = await apiClient.post('/api/user/cdn/cert-library/apply', {
        cert_id: certId,
        hostname_id: hostnameId
      });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  // ==========================================
  // Origin CA Certificate Management
  // ==========================================

  /**
   * Create an Origin CA certificate
   * @param {object} data - { hostnames: string[], key_type: string, validity_days: number, domain_id?: number }
   */
  async createOriginCert(data) {
    try {
      const response = await apiClient.post('/api/user/cdn/origin-cert/create', data);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * List Origin CA certificates
   * @param {number} page
   * @param {number} perPage
   */
  async getOriginCerts(page = 1, perPage = 20, domainId = null) {
    try {
      const params = { page, per_page: perPage };
      // 只列当前域的 Origin 证书(域设置页),不传=账户全部。
      if (domainId != null) params.domain_id = domainId;
      const response = await apiClient.get('/api/user/cdn/origin-cert/list', { params });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Get a single Origin CA certificate
   * @param {number} id
   */
  async getOriginCert(id) {
    try {
      const response = await apiClient.get('/api/user/cdn/origin-cert/get', {
        params: { id }
      });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Revoke an Origin CA certificate
   * @param {number} id
   */
  async revokeOriginCert(id) {
    try {
      const response = await apiClient.post('/api/user/cdn/origin-cert/revoke', { id });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Delete an Origin CA certificate
   * @param {number} id
   */
  async deleteOriginCert(id) {
    try {
      const response = await apiClient.delete('/api/user/cdn/origin-cert/delete', {
        params: { id }
      });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Get the Origin CA root certificate
   */
  async getOriginCaCert() {
    try {
      const response = await apiClient.get('/api/user/cdn/origin-cert/ca');
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Run diagnostics on a hostname
   * @param {Object} params - { hostname: string } or { url: string }
   */
  async runDiagnostics(params) {
    try {
      // 后台执行:入队拿 id,轮询结果(通常 10-15s)
      const started = await apiClient.post('/api/user/cdn/diagnostics/run', params);
      if (!started.data?.success) return started.data;
      const id = started.data.data?.id;
      for (let i = 0; i < 60; i++) {
        await new Promise((r) => setTimeout(r, 1500));
        const res = await apiClient.get('/api/user/cdn/diagnostics/result', { params: { id } });
        if (!res.data?.success) return res.data;
        if (res.data.data?.status === 'done') return { success: true, data: res.data.data.result };
      }
      return { success: false };
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  // ==========================================
  // Firewall Rules (Full CRUD)
  // ==========================================

  async getFirewallRules(params) {
    try {
      const response = await apiClient.get('/api/user/cdn/firewall-rules', { params });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  async getFirewallRule(params) {
    try {
      const response = await apiClient.get('/api/user/cdn/firewall-rules/get', { params });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  async createFirewallRule(data) {
    try {
      const response = await apiClient.post('/api/user/cdn/firewall-rules/create', data);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  async updateFirewallRule(data) {
    try {
      const response = await apiClient.post('/api/user/cdn/firewall-rules/update', data);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  async deleteFirewallRule(data) {
    try {
      const response = await apiClient.delete('/api/user/cdn/firewall-rules/delete', { data });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  // ==========================================
  // WAF Analytics
  // ==========================================

  async getWafOverview(params) {
    try {
      const response = await apiClient.get('/api/user/waf-analytics/overview', { params });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  async getWafTimeline(params) {
    try {
      const response = await apiClient.get('/api/user/waf-analytics/timeline', { params });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  async getWafTopRules(params) {
    try {
      const response = await apiClient.get('/api/user/waf-analytics/top-rules', { params });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  async getWafTopIPs(params) {
    try {
      const response = await apiClient.get('/api/user/waf-analytics/top-ips', { params });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  async getWafLogs(params) {
    try {
      const response = await apiClient.get('/api/user/waf-analytics/logs', { params });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }
}

export default new CDNService();
